import { useSearchParams } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NavigationContext } from "./NavigationContext";
import HumanResources from "./pages/HumanResources";
import Bookkeeping from "./pages/Bookkeeping";
import TaxServices from "./pages/TaxServices";
import CompanyFormation from "./pages/CompanyFormation";
import PatentServices from "./pages/PatentServices";
import BusinessInsurance from "./pages/BusinessInsurance";
import LegalServices from "./pages/LegalServices";
import LegalBusinessContracts from "./pages/LegalBusinessContracts";
import LegalIPPropertyProtection from "./pages/LegalIPPropertyProtection";
import LegalEntityFormation from "./pages/LegalEntityFormation";
import LegalRegulatoryCompliance from "./pages/LegalRegulatoryCompliance";
import LegalEmploymentLaw from "./pages/LegalEmploymentLaw";
import LegalDisputeResolution from "./pages/LegalDisputeResolution";
import SAMGov from "./pages/SAMGov";
import UEIApplication from "./pages/UEIApplication";
import SAMGovProfileSetup from "./pages/SAMGovProfileSetup";
import AnnualRenewal from "./pages/AnnualRenewal";
import Certifications from "./pages/Certifications";
import EntityValidation from "./pages/EntityValidation";
import BizServePortal from "./pages/BizServePortal";
import WebsiteDevelopment from "./pages/WebsiteDevelopment";
import CMMCCybersecurity from "./pages/CMMCCybersecurity";
import NotFound from "./pages/NotFound";
import "./index.css";
// import './App.css'
import ShadowWrapper from "ShadowWrapper";

const queryClient = new QueryClient();

interface DomainLandingPagesAppProps {
  domain_id: number;
}

// Slug → component map for Legal Services sub-pages
const legalServicePages: Record<string, React.ReactElement> = {
  "contracts":              <LegalBusinessContracts />,
  "ip-protection":          <LegalIPPropertyProtection />,
  "entity-formation":       <LegalEntityFormation />,
  "regulatory-compliance":  <LegalRegulatoryCompliance />,
  "employment-law":         <LegalEmploymentLaw />,
  "dispute-resolution":     <LegalDisputeResolution />,
};

// Slug → component map for SAM.gov sub-pages
const samGovServicePages: Record<string, React.ReactElement> = {
  "uei-application":   <UEIApplication />,
  "profile-setup":     <SAMGovProfileSetup />,
  "annual-renewal":    <AnnualRenewal />,
  "certifications":    <Certifications />,
  "entity-validation": <EntityValidation />,
};

const DomainLandingPagesApp: React.FC<DomainLandingPagesAppProps> = ({
  domain_id,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // For Legal Services (domain_id 7), sub-pages are driven by ?service=slug
  const serviceSlug = (searchParams.get("service") || "").toLowerCase();

  const navigateTo = (slug: string) => {
    if (!slug) {
      // Empty slug = back to root page, clear the param
      setSearchParams({});
    } else {
      setSearchParams({ service: slug });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageComponent = () => {
    // If a service slug is present, render the matching Legal sub-page
    if (serviceSlug && legalServicePages[serviceSlug]) {
      return legalServicePages[serviceSlug];
    }

    // If a service slug is present for SAM.gov (domain_id 10), render the matching sub-page
    if (serviceSlug && domain_id === 10 && samGovServicePages[serviceSlug]) {
      return samGovServicePages[serviceSlug];
    }

    switch (domain_id) {
      case 1:
        return <HumanResources />;
      case 2:
        return <Bookkeeping />;
      case 3:
        return <TaxServices />;
      case 4:
        return <CompanyFormation />;
      case 5:
        return <PatentServices />;
      case 6:
        return <BusinessInsurance />;
      case 7:
        return <LegalServices />;
      case 8:
        return <LegalBusinessContracts />;
      case 9:
        return <LegalIPPropertyProtection />;
      case 10:
        return <SAMGov />;
      case 11:
        return <BizServePortal />;
      case 12:
        return <WebsiteDevelopment />;
      case 13:
        return <CMMCCybersecurity />;
      default:
        return <NotFound />;
    }
  };

  return (
    <ShadowWrapper>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <NavigationContext.Provider value={{ navigateTo }}>
            {getPageComponent()}
          </NavigationContext.Provider>
        </TooltipProvider>
      </QueryClientProvider>
    </ShadowWrapper>
  );
};

export default DomainLandingPagesApp;
